import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { purchaseRequestsApi } from '../purchaseRequestsApi';
import type { PurchaseRequestDetail } from '../purchaseRequestTypes';

/** PDF, PNG or JPEG at 10 MB — the allowlist and the cap the endpoint enforces. */
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = 'application/pdf,.pdf,image/png,.png,image/jpeg,.jpg,.jpeg';

/**
 * Files the Sales Quotation against a request.
 *
 * **A modal because it is a transaction over a file**, and because replacing one supersedes
 * whatever is already filed — which the body says before the tick.
 */
export function AttachQuotationModal({
  open,
  request,
  onClose,
  onAttached,
}: {
  open: boolean;
  request: PurchaseRequestDetail;
  onClose: () => void;
  onAttached: (next: PurchaseRequestDetail) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState<ApiError | null>(null);

  const tooLarge = file !== null && file.size > MAX_BYTES;
  const canSave = file !== null && !tooLarge && confirmed && !saving;

  async function save() {
    if (!canSave || file === null) return;

    setSaving(true);
    setError(null);
    setPercent(0);

    try {
      onAttached(await purchaseRequestsApi.attachQuotation(request.id, file, setPercent));
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not file the quotation.', 0),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Sales quotation for ${request.requestNumber}`}
      size="lg"
      id="purchase-request-quotation-modal"
      /*
        NO Autogenerate. A quotation is a document a supplier issued — the binary the server
        verifies by magic bytes — and a fabricated one would claim to be something it is not,
        which is the case the generator rule names as off limits.
      */
      confirm={{
        id: 'purchase-request-quotation-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="purchase-request-quotation-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="purchase-request-quotation-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            /*
                  THE ONE LOADING LABEL THAT IS NOT "Submitting". It carries REAL upload progress,
                  which is the whole reason `apiUpload` exists — a figure the reader can watch is
                  not chrome, and flattening it to a word would be the fake-progress failure in
                  reverse: hiding a measurement the product actually has.
                */
                loadingLabel={percent > 0 ? `Filing ${percent}%` : 'Submitting'}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div>
          <label
            htmlFor="purchase-request-quotation-file"
            className="mb-1.5 block text-sm font-medium text-slate-700"
          >
            Quotation<span className="ml-0.5 text-red-600">*</span>
          </label>

          <input
            id="purchase-request-quotation-file"
            name="purchase-request-quotation-file"
            type="file"
            accept={ACCEPT}
            disabled={saving}
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            className="block w-full rounded-lg border border-slate-300 bg-white text-sm text-slate-600 file:mr-3 file:cursor-pointer file:rounded-l-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:text-slate-700 hover:file:bg-slate-200 disabled:cursor-not-allowed disabled:bg-slate-50"
          />

          <p className="mt-1.5 text-xs text-slate-500">
            PDF, PNG or JPEG, up to {formatBytes(MAX_BYTES)}. It is filed into the Repository
            like every other attachment, and the type is verified against the file's own leading
            bytes rather than its name.
          </p>

          {tooLarge ? (
            <p className="mt-1 text-xs text-red-700">
              That file is {formatBytes(file.size)}, over the {formatBytes(MAX_BYTES)} limit.
            </p>
          ) : null}
        </div>

        {/*
          REPLACING SUPERSEDES, and it is stated before the tick rather than discovered: the
          endpoint files the new document and points the request at it, so the one already filed
          stops being the request's quotation.
        */}
        {request.quotationRepositoryItemId ? (
          <p className="text-xs text-slate-600">
            This request already has a quotation filed. Filing another one replaces it as the
            request's quotation — the superseded document stays in the Repository.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
